import { type SelectOption } from '@framework/form/inputs/SelectInput.tsx';
import { Button, Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TbIcon } from '../../../icon/TbIcon.tsx';
import { useSortDirectionOptions } from '../hooks/useSortDirectionOptions.ts';
import { type SortDirection } from '../utils/sortValue.ts';

const SortPopoverContainer = styled.div`
  min-width: 260px;
  display: flex;
  flex-direction: column;
  /* Compensa il padding interno di Ant Design Popover (come l'editor dei chip). */
  margin: -12px;
`;

const SortPopoverFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

const SortPopoverFieldLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.colorTextSecondary};
`;

const SortPopoverFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px;
  border-top: 1px solid ${props => props.theme.colorSplit};
`;

export interface IFilterBarSortPopoverContentProps {
  /** Campi ordinabili: `value` = chiave query, `label` = etichetta mostrata. */
  sortingFields: SelectOption[];
  /** Campo attualmente selezionato nella bozza (`null` = nessuno). */
  field: string | null;
  /** Direzione attualmente selezionata nella bozza. */
  direction: SortDirection;
  onFieldChange: (field: string) => void;
  onDirectionChange: (direction: SortDirection) => void;
  onApply: () => void;
  onCancel: () => void;
}

/**
 * Contenuto del popover di ordinamento: due Select (campo + direzione) e i
 * pulsanti Annulla/Applica. Componente PRESENTAZIONALE/controllato — non
 * possiede stato né tocca il form: riceve i valori e ne espone i `onChange`,
 * così la logica (bozza + scrittura su `ordinamenti`) resta tutta nel genitore
 * [FilterBarSortPopover].
 */
export function FilterBarSortPopoverContent({
  sortingFields,
  field,
  direction,
  onFieldChange,
  onDirectionChange,
  onApply,
  onCancel,
}: Readonly<IFilterBarSortPopoverContentProps>) {
  const { t } = useTranslation();
  const directionOptions = useSortDirectionOptions();

  // Etichette definite UNA sola volta: riusate per la caption visibile E per
  // l'`aria-label` della stessa Select.
  const fieldLabel = t('filter-bar.sort.field-label', 'Campo da ordinare');
  const directionLabel = t(
    'filter-bar.sort.direction-label',
    'Direzione ordinamento',
  );

  return (
    <SortPopoverContainer>
      <SortPopoverFields>
        <div>
          <SortPopoverFieldLabel>{fieldLabel}</SortPopoverFieldLabel>
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            placeholder={t(
              'filter-bar.sort.field-placeholder',
              'Seleziona un campo…',
            )}
            aria-label={fieldLabel}
            value={field ?? undefined}
            options={sortingFields}
            onChange={(value: string) => onFieldChange(value)}
          />
        </div>
        <div>
          <SortPopoverFieldLabel>{directionLabel}</SortPopoverFieldLabel>
          <Select
            style={{ width: '100%' }}
            aria-label={directionLabel}
            value={direction}
            onChange={(value: SortDirection) => onDirectionChange(value)}
            options={directionOptions.map(option => ({
              value: option.value,
              label: (
                <Space>
                  <TbIcon icon={option.icon} />
                  {option.label}
                </Space>
              ),
            }))}
          />
        </div>
      </SortPopoverFields>
      <SortPopoverFooter>
        <Button onClick={onCancel}>{t('filter-bar.cancel', 'Annulla')}</Button>
        <Button type="primary" onClick={onApply}>
          {t('filter-bar.apply', 'Applica')}
        </Button>
      </SortPopoverFooter>
    </SortPopoverContainer>
  );
}
